/**
 * The 2D twin map most hosts want, in one component: a `TurboTwinMap` with the
 * twins' clustered markers, the shapes of their Geospatial values and the
 * relationship lines between them, wired together. A relationship line ends on
 * the twin's marker (the cluster bubble that holds it, when clustered), else on
 * the twin's first shape (`geoAnchorOf`), and the lines are drawn again when
 * markers move or clusters re-form. The host passes data and callbacks.
 *
 * The host's own layers (a camera controller, an editor) are `children`: they
 * are added to the map before the twin layers. A map that differs composes
 * `TurboTwinMap` and the layers itself. The needs are those of
 * `ClusteredMarkersLayer`: `leaflet.markercluster`, and the host imports
 * `leaflet/dist/leaflet.css` and `leaflet.markercluster/dist/MarkerCluster.css`.
 */
import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
  useState,
  type ForwardedRef,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
} from 'react';
import {
  geoAnchorOf,
  type GeoHoverTarget,
  type GeoObject,
  type GeoShapeColors,
} from '../geoObjects';
import type { LatLngLike, RelationshipEdge } from '../relationshipEdges';
import type { RelPick } from '../relPick';
import {
  ClusteredMarkersLayer,
  type ClusteredMarkersHandle,
  type ClusterColors,
  type TwinMarkerEntry,
} from './ClusteredMarkersLayer';
import { GeoShapesLayer } from './GeoShapesLayer';
import { MARKER_COLORS, type MarkerColors } from './makeMarkerIcon';
import { RelationshipLinesLayer } from './RelationshipLinesLayer';
import { TurboTwinMap, type TurboTwinMapProps } from './TurboTwinMap';

/** A twin on the map: what its marker needs (`TwinMarkerEntry`) and its Geospatial shapes (`extractGeoObjects`). Any object with these fields works, and every callback gets it back whole. */
export interface TwinMapTwin extends TwinMarkerEntry {
  geoObjects: readonly GeoObject[];
}

/** The ref of a `TwinMap` is the handle of its markers: `zoomToTwin`, `endpointOf` and `revealTwin`. */
export type TwinMapHandle = ClusteredMarkersHandle;

/**
 * The shell's own props (`layer`, `layerControl`, `center`, `zoom`, `maxZoom`, `fitBounds`, `fitKey`,
 * `fitOptions`, `zoomControl`, `scrollWheelZoom`, `className`, `style`) go to the `TurboTwinMap` as they are.
 */
export interface TwinMapProps<T extends TwinMapTwin = TwinMapTwin> extends Omit<
  TurboTwinMapProps,
  'children'
> {
  /** Every twin: the shapes are drawn from these, and a line to a twin with no marker ends on the first of its shapes. */
  twins: readonly T[];
  /** The twins drawn as markers; a twin with no `position` gets none. The host filters here: by a mode, say, or to leave out a twin an editor draws itself. */
  markers: readonly T[];

  /** A twin's marker colours (`ClusteredMarkersLayer`'s `colorOf`). Keep the function stable (module level or `useCallback`): a new one restyles every marker. */
  markerColorOf?: (twin: T) => MarkerColors;
  /** Cluster bubble colours; each one left out keeps its default (`CLUSTER_COLORS`). */
  clusterColors?: Partial<ClusterColors>;
  /** The selected twin: its marker is ringed and out of the clusters, and its shapes are drawn thicker in the selection colour. */
  selectedTwinId?: string | null;
  /** A marker click. */
  onTwinClick?: (twinId: string) => void;
  /** A marker right-click, at the pointer's client position (`x`, `y`). Without it the browser's own menu opens. */
  onTwinContextMenu?: (twinId: string, x: number, y: number) => void;
  /** A relationship pick in progress (`relPickOf`): the valid twins ring green, the others dim. Null: none. */
  pick?: RelPick | null;
  /** A name pill under every marker. Default false. */
  labels?: boolean;

  /** Draw the twins' shapes. Default true. */
  showShapes?: boolean;
  /** The shapes drawn for a twin; default its `geoObjects`. A host whose editor draws one shape itself leaves it out here (`withoutGeoPart`). */
  shapesOf?: (twin: T) => readonly GeoObject[];
  /** Stroke and fill colour of the shapes; default the default marker colour (`MARKER_COLORS.fill`). */
  shapeColor?: string;
  /** Selection and hover colours of the shapes; each one left out keeps its default (`GEO_SHAPE_COLORS`). */
  shapeColors?: Partial<GeoShapeColors>;
  /** A twin's shape colour, over `shapeColor`, selected or not (selection draws the shapes thicker, over
   *  `shapeColors.selected`): the 3D viewers draw a twin's shapes in its model colour, so the host passes
   *  its marker fill here and a shape never reads as a relationship line. Keep the function stable. */
  shapeColorOf?: (twin: T) => string;
  /**
   * A hook the map calls once for every twin that has shapes, with the twin's id: the property or part it
   * returns is drawn in the hover colour, say the menu row under the pointer (`GeoShapesLayer`'s
   * `highlight`). Each twin's layer calls it itself, so a hover renders only the layers whose answer changes.
   * It must be a stable hook, defined at module level and the same on every render: a different one breaks the order of hooks.
   */
  useShapeHighlight?: (twinId: string) => GeoHoverTarget | null;
  /** Tooltip content for the shapes of `geo`, one of `twin`'s Geospatial values; shapes have no tooltip without it. */
  renderShapeTooltip?: (twin: T, geo: GeoObject) => ReactNode;
  /** A shape click: the twin, and the property and part the shape draws. */
  onShapeClick?: (twinId: string, propertyId: bigint, propertyName: string, part: number) => void;
  /** A shape right-click, at viewport (`x`, `y`): the twin, and the property and part. */
  onShapeContextMenu?: (
    twinId: string,
    propertyName: string,
    part: number,
    x: number,
    y: number,
  ) => void;

  /** The relationships to draw (`relationshipEdges`). Without it there is no lines layer; an empty list draws nothing. */
  relationships?: readonly RelationshipEdge[];
  /** A line's colour (`RELATIONSHIP_LINE_COLOR` by default). Keep the function stable: a new one redraws every line. */
  relationshipColorOf?: (edge: RelationshipEdge) => string;
  /** A line click: the source twin and the relationship. */
  onRelationshipClick?: (sourceTwinId: string, relationshipId: bigint) => void;
  /** A line right-click at viewport (`x`, `y`): every relationship the line draws. */
  onRelationshipContextMenu?: (edges: RelationshipEdge[], x: number, y: number) => void;

  /** The host's own layers, added to the map before the shapes, the markers and the lines. */
  children?: ReactNode;
}

// What `useShapeHighlight` stands for when the host gives none: no shape is lit from outside.
const useNoHighlight = (): GeoHoverTarget | null => null;

interface TwinShapesProps<T extends TwinMapTwin> {
  twin: T;
  selected: boolean;
  color: string;
  colors: Partial<GeoShapeColors> | undefined;
  shapesOf: ((twin: T) => readonly GeoObject[]) | undefined;
  useHighlight: (twinId: string) => GeoHoverTarget | null;
  renderTooltip: ((twin: T, geo: GeoObject) => ReactNode) | undefined;
  onClick: TwinMapProps<T>['onShapeClick'];
  onContextMenu: TwinMapProps<T>['onShapeContextMenu'];
}

/** One twin's shapes: a `GeoShapesLayer` with the twin bound into the host's callbacks and its own highlight read. */
function TwinShapes<T extends TwinMapTwin>({
  twin,
  selected,
  color,
  colors,
  shapesOf,
  useHighlight,
  renderTooltip,
  onClick,
  onContextMenu,
}: TwinShapesProps<T>) {
  const { twinId } = twin;
  const highlight = useHighlight(twinId);
  return (
    <GeoShapesLayer
      geoObjects={shapesOf ? shapesOf(twin) : twin.geoObjects}
      selected={selected}
      color={color}
      highlight={highlight}
      colors={colors}
      renderTooltip={renderTooltip && ((geo) => renderTooltip(twin, geo))}
      onGeoClick={onClick && ((id, name, part) => onClick(twinId, id, name, part))}
      onGeoContextMenu={
        onContextMenu && ((name, part, x, y) => onContextMenu(twinId, name, part, x, y))
      }
    />
  );
}

/** The twin map: the shell with the markers, the shapes and the lines; the ref is the markers' `TwinMapHandle`. */
export const TwinMap = forwardRef(function TwinMap<T extends TwinMapTwin>(
  {
    twins,
    markers,
    markerColorOf,
    clusterColors,
    selectedTwinId = null,
    onTwinClick,
    onTwinContextMenu,
    pick,
    labels,
    showShapes = true,
    shapesOf,
    shapeColor = MARKER_COLORS.fill,
    shapeColors,
    shapeColorOf,
    useShapeHighlight,
    renderShapeTooltip,
    onShapeClick,
    onShapeContextMenu,
    relationships,
    relationshipColorOf,
    onRelationshipClick,
    onRelationshipContextMenu,
    children,
    ...shell
  }: TwinMapProps<T>,
  ref: ForwardedRef<TwinMapHandle>,
) {
  // The lines ask the markers where a twin shows, and the host gets the same handle through the ref.
  const markersRef = useRef<ClusteredMarkersHandle | null>(null);
  const setMarkersRef = useCallback(
    (handle: ClusteredMarkersHandle | null) => {
      markersRef.current = handle;
      if (typeof ref === 'function') ref(handle);
      else if (ref) ref.current = handle;
    },
    [ref],
  );

  // Where a line ends depends on how the markers are clustered right now: each layout change bumps the
  // version, and the lines are drawn again. Only the lines read it, so without them a change renders nothing.
  const [layoutVersion, setLayoutVersion] = useState(0);
  const onLayoutChange = useCallback(() => setLayoutVersion((v) => v + 1), []);

  // The marker, or the cluster bubble that holds it; else the twin's first shape. A twin with neither gets no line.
  const twinById = useMemo(() => new Map(twins.map((t) => [t.twinId, t] as const)), [twins]);
  const endpointOf = useCallback(
    (twinId: string): LatLngLike | null =>
      markersRef.current?.endpointOf(twinId) ?? geoAnchorOf(twinById.get(twinId)?.geoObjects),
    [twinById],
  );

  return (
    <TurboTwinMap {...shell}>
      {children}
      {showShapes &&
        twins.map((twin) => {
          if (twin.geoObjects.length === 0) return null;
          const color = shapeColorOf ? shapeColorOf(twin) : shapeColor;
          return (
            <TwinShapes
              key={`geo-${twin.twinId}`}
              twin={twin}
              selected={selectedTwinId === twin.twinId}
              color={color}
              colors={shapeColorOf ? { ...shapeColors, selected: color } : shapeColors}
              shapesOf={shapesOf}
              useHighlight={useShapeHighlight ?? useNoHighlight}
              renderTooltip={renderShapeTooltip}
              onClick={onShapeClick}
              onContextMenu={onShapeContextMenu}
            />
          );
        })}
      <ClusteredMarkersLayer
        ref={setMarkersRef}
        entries={markers}
        colorOf={markerColorOf}
        selectedTwinId={selectedTwinId}
        onClick={onTwinClick}
        onContextMenu={onTwinContextMenu}
        onLayoutChange={relationships ? onLayoutChange : undefined}
        pick={pick}
        labels={labels}
        colors={clusterColors}
      />
      {relationships && (
        <RelationshipLinesLayer
          edges={relationships}
          endpointOf={endpointOf}
          layoutVersion={layoutVersion}
          colorOf={relationshipColorOf}
          onEdgeClick={onRelationshipClick}
          onEdgeContextMenu={onRelationshipContextMenu}
        />
      )}
    </TurboTwinMap>
  );
}) as <T extends TwinMapTwin>(
  props: TwinMapProps<T> & RefAttributes<TwinMapHandle>,
) => ReactElement | null;
